import React, { useState, useRef } from 'react';
import './inputs.scss';

/**
 * FancyTextField — text input with animated floating label.
 *
 * API surface mirrors MUI TextField for trivial migration:
 *   <FancyTextField label="Name" value={v} onChange={(e) => setV(e.target.value)} />
 *
 * Props:
 *   label, value, onChange, placeholder, helperText, error, disabled,
 *   fullWidth, multiline, rows, type ('text' | 'email' | 'password' | …),
 *   startAdornment, endAdornment, style, className, inputRef
 */
export const FancyTextField = React.forwardRef(function FancyTextField(
  {
    label,
    value,
    onChange,
    placeholder,
    helperText,
    error = false,
    disabled = false,
    fullWidth = false,
    multiline = false,
    rows = 3,
    type = 'text',
    startAdornment,
    endAdornment,
    autoFocus = false,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef,
    style,
    className = '',
    ...rest
  },
  ref
) {
  const [focused, setFocused] = useState(false);
  const filled = value !== undefined && value !== null && value !== '';
  const floating = focused || filled;
  const localRef = useRef();
  const finalRef = inputRef || localRef;

  const handleFieldClick = () => {
    if (!disabled && finalRef.current) finalRef.current.focus();
  };

  const InputElement = multiline ? 'textarea' : 'input';

  const classes = [
    'fancy-tf',
    fullWidth ? 'fancy-tf--full' : '',
    multiline ? 'fancy-tf--multiline' : '',
    error ? 'fancy-tf--error' : '',
    disabled ? 'fancy-tf--disabled' : '',
    focused ? 'fancy-tf--focused' : '',
    floating ? 'fancy-tf--floating' : '',
    label ? 'fancy-tf--has-label' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div ref={ref} className={classes} style={style}>
      <div className="fancy-tf__field" onClick={handleFieldClick}>
        {startAdornment && (
          <span className="fancy-tf__adornment fancy-tf__adornment--start">{startAdornment}</span>
        )}
        <div className="fancy-tf__inner">
          {label && <label className="fancy-tf__label">{label}</label>}
          <InputElement
            ref={finalRef}
            className="fancy-tf__input"
            type={multiline ? undefined : type}
            value={value ?? ''}
            onChange={onChange}
            placeholder={floating ? placeholder : ''}
            disabled={disabled}
            autoFocus={autoFocus}
            rows={multiline ? rows : undefined}
            onFocus={(e) => { setFocused(true); if (onFocus) onFocus(e); }}
            onBlur={(e) => { setFocused(false); if (onBlur) onBlur(e); }}
            onKeyDown={onKeyDown}
            {...rest}
          />
        </div>
        {endAdornment && (
          <span className="fancy-tf__adornment fancy-tf__adornment--end">{endAdornment}</span>
        )}
      </div>
      {helperText && (
        <div className={`fancy-tf__helper ${error ? 'fancy-tf__helper--error' : ''}`}>{helperText}</div>
      )}
    </div>
  );
});
